import { forwardRef, useState, type ChangeEvent } from "react";
import { inputShell } from "./Field";
import { COUNTRIES, type CountryDialInfo } from "../lib/phone";

interface PhoneInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  country: CountryDialInfo;
  onCountryChange: (country: CountryDialInfo) => void;
  invalid?: boolean | undefined;
  autoFocus?: boolean | undefined;
}

/**
 * Mobile number entry, with the country chosen rather than assumed.
 *
 * The dial code is a picker, not editable text: v1 stored the same number four
 * different ways (`08031234567`, `+2348031234567`, `0803 123 4567`), which
 * broke duplicate detection during the migration. A caller cannot type a
 * country code here, so v2 only ever stores one shape.
 *
 * It is a picker rather than a fixed `+234` because the migrated data already
 * holds Ghanaian and Rwandan students — hardcoding Nigeria locked out accounts
 * that already existed.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { id, value, onChange, country, onCountryChange, invalid = false, autoFocus },
  ref,
) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const handle = (event: ChangeEvent<HTMLInputElement>) => {
    // Digits only, capped at the trunk zero plus the national length.
    onChange(event.target.value.replace(/\D/g, "").slice(0, country.nationalLength + 1));
  };

  return (
    <div className="relative">
      <div className={`${inputShell} ${invalid ? "border-negative" : "border-border-strong"}`}>
        <button
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          aria-label={`Country: ${country.name}. Change`}
          aria-expanded={pickerOpen}
          className="tabular flex shrink-0 items-center gap-1 rounded-[var(--radius-chip)] border border-border bg-surface-high px-2.5 py-[5px] text-[13px] font-semibold"
        >
          <span aria-hidden="true">{country.flag}</span>+{country.dialCode}
          <span aria-hidden="true" className="text-[9px] text-text-muted">
            ▼
          </span>
        </button>
        <input
          id={id}
          ref={ref}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          aria-invalid={invalid}
          aria-describedby={invalid ? `${id}-error` : undefined}
          className="tabular w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted"
          placeholder={country.example}
          value={formatNationalDigits(value)}
          onChange={handle}
        />
      </div>

      {pickerOpen ? (
        <ul
          role="listbox"
          aria-label="Country"
          className="absolute left-0 top-full z-20 mt-1 w-full list-none overflow-hidden rounded-[var(--radius-control)] border border-border-strong bg-surface p-0 shadow-lg"
        >
          {COUNTRIES.map((option) => (
            <li key={option.iso}>
              <button
                type="button"
                role="option"
                aria-selected={option.iso === country.iso}
                onClick={() => {
                  onCountryChange(option);
                  // The old digits belong to the old country's format.
                  onChange("");
                  setPickerOpen(false);
                }}
                className={`flex min-h-11 w-full items-center gap-2.5 px-3.5 text-left text-[14px] ${
                  option.iso === country.iso ? "bg-accent-tint font-semibold" : ""
                }`}
              >
                <span aria-hidden="true">{option.flag}</span>
                {option.name}
                <span className="tabular ml-auto text-[12.5px] text-text-muted">
                  +{option.dialCode}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
});

/** `08031234567` → `0803 123 4567`. Presentation only. */
export function formatNationalDigits(digits: string): string {
  return (digits.match(/.{1,4}|.+/g) ?? [])
    .reduce<string[]>((groups, _chunk, index) => {
      // 4-3-4 for an 11-digit local number, 3-3-3 for a 9-digit one.
      const sizes = digits.length > 10 ? [4, 3, 4] : [3, 3, 3, 3];
      if (index > 0) return groups;
      let offset = 0;
      for (const size of sizes) {
        if (offset >= digits.length) break;
        groups.push(digits.slice(offset, offset + size));
        offset += size;
      }
      return groups;
    }, [])
    .join(" ")
    .trim();
}
